/**
 * Agent işe giriş tarihi — Excel toplu import
 */
import * as XLSX from "xlsx";

const cellStr = (v) => (v === null || v === undefined ? "" : String(v).trim());

const normalizeTr = (s) => cellStr(s).toLowerCase()
  .replace(/ı/g, "i").replace(/İ/g, "i").replace(/ş/g, "s").replace(/ğ/g, "g")
  .replace(/ü/g, "u").replace(/ö/g, "o").replace(/ç/g, "c");

const pad2 = (n) => String(n).padStart(2, "0");

function isoFromParts(y, m, d) {
  if (y < 1990 || y > 2100 || m < 1 || m > 12 || d < 1 || d > 31) return null;
  return `${y}-${pad2(m)}-${pad2(d)}`;
}

/** Excel serial — takvim günü (UTC); locale/SSF bağımsız */
function parseExcelSerial(n) {
  if (!Number.isFinite(n) || n < 1000) return null;
  const dc = XLSX.SSF?.parse_date_code?.(n);
  if (dc && dc.y >= 1990 && dc.y <= 2100) {
    return isoFromParts(dc.y, dc.m, dc.d);
  }
  const ms = Math.round((n - 25569) * 86400000);
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return null;
  return isoFromParts(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}

/** JS Date — yerel gün/ay (Türkçe Excel + cellDates) */
function parseJsDateLocal(v) {
  if (!(v instanceof Date) || Number.isNaN(v.getTime())) return null;
  return isoFromParts(v.getFullYear(), v.getMonth() + 1, v.getDate());
}

/**
 * Metin: nokta = TR gün.ay.yıl (16.03.2026)
 * Slash: önce TR gün/ay; ay>12 veya gün>12 ise otomatik ayır (Excel bazen 3/16/2026 verir)
 */
function parseTrDateString(s) {
  const raw = cellStr(s);
  if (!raw) return null;

  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  const dot4 = raw.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (dot4) {
    const day = parseInt(dot4[1], 10);
    const month = parseInt(dot4[2], 10);
    const year = parseInt(dot4[3], 10);
    return isoFromParts(year, month, day);
  }
  const dot2 = raw.match(/^(\d{1,2})\.(\d{1,2})\.(\d{2})$/);
  if (dot2) {
    const day = parseInt(dot2[1], 10);
    const month = parseInt(dot2[2], 10);
    let year = parseInt(dot2[3], 10);
    year += year < 50 ? 2000 : 1900;
    return isoFromParts(year, month, day);
  }

  const slashY = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (slashY) {
    let a = parseInt(slashY[1], 10);
    let b = parseInt(slashY[2], 10);
    let year = parseInt(slashY[3], 10);
    if (slashY[3].length === 2) {
      year += year < 50 ? 2000 : 1900;
    }
    let day;
    let month;
    if (a > 12 && b <= 12) {
      day = a;
      month = b;
    } else if (b > 12 && a <= 12) {
      month = a;
      day = b;
    } else {
      day = a;
      month = b;
    }
    return isoFromParts(year, month, day);
  }

  const dash4 = raw.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (dash4) {
    const day = parseInt(dash4[1], 10);
    const month = parseInt(dash4[2], 10);
    const year = parseInt(dash4[3], 10);
    return isoFromParts(year, month, day);
  }

  return null;
}

/** Panel / DB — boş, sıfır veya geçersiz işe giriş */
export function normalizePanelHireDate(value) {
  if (value === null || value === undefined) return null;
  const s = cellStr(value);
  if (!s || s === "0000-00-00" || s.startsWith("0000-00-00")) return null;
  const d = parseHireDateCell(value);
  if (!d || d.startsWith("0000-")) return null;
  return d;
}

export function parseHireDateCell(v) {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number" && Number.isFinite(v)) {
    const fromSerial = parseExcelSerial(v);
    if (fromSerial) return fromSerial;
  }
  const fromDate = parseJsDateLocal(v);
  if (fromDate) return fromDate;
  return parseTrDateString(v);
}

/** Tarih hücresi: serial > TR noktalı metin > slash metin (Date/UTC kullanma) */
export function pickHireDateCellValue(rawVal, fmtVal) {
  if (typeof rawVal === "number" && Number.isFinite(rawVal) && parseExcelSerial(rawVal)) {
    return rawVal;
  }
  const fmtS = cellStr(fmtVal);
  if (fmtS && /^\d{1,2}[./]\d{1,2}[./]/.test(fmtS)) return fmtVal;
  if (typeof fmtVal === "number" && Number.isFinite(fmtVal) && parseExcelSerial(fmtVal)) {
    return fmtVal;
  }
  if (typeof rawVal === "number" && Number.isFinite(rawVal)) return rawVal;
  if (fmtVal != null && fmtVal !== "" && !(fmtVal instanceof Date)) return fmtVal;
  if (rawVal instanceof Date) {
    return `${rawVal.getFullYear()}-${pad2(rawVal.getMonth() + 1)}-${pad2(rawVal.getDate())}`;
  }
  return rawVal;
}

function normalizeHeader(h) {
  return normalizeTr(cellStr(h));
}

export function guessColumnMapping(headerRow) {
  let nameCol = -1;
  let dateCol = -1;
  (headerRow || []).forEach((cell, idx) => {
    const h = normalizeHeader(cell);
    if (!h) return;
    if (dateCol < 0 && (
      /ise\s*giris|isegiris|is\s*e\s*giris|baslangic|baslang|giris\s*tarih|hire|start\s*date|employment/.test(h)
      || (h.includes("tarih") && (h.includes("giris") || h.includes("ise") || h.includes("bas")))
    )) {
      dateCol = idx;
    }
    if (nameCol < 0 && (
      /ad\s*soyad|asistan|agent|personel|isim|name|soyad/.test(h)
      && !h.includes("tarih") && !h.includes("kullanici")
    )) {
      nameCol = idx;
    }
  });
  if (nameCol < 0) {
    (headerRow || []).forEach((cell, idx) => {
      const h = normalizeHeader(cell);
      if (nameCol < 0 && h.includes("asistan")) nameCol = idx;
    });
  }
  return { nameCol, dateCol };
}

function findHeaderRowIndex(rows) {
  for (let r = 0; r < Math.min(rows.length, 25); r++) {
    const line = rows[r] || [];
    const { nameCol, dateCol } = guessColumnMapping(line);
    if (nameCol >= 0 && dateCol >= 0) return r;
    if (nameCol >= 0 && line.some((c) => parseHireDateCell(c))) return r;
  }
  for (let r = 0; r < Math.min(rows.length, 25); r++) {
    const { nameCol } = guessColumnMapping(rows[r] || []);
    if (nameCol >= 0) return r;
  }
  return 0;
}

export function parseAgentHireWorkbook(arrayBuffer) {
  const bytes = arrayBuffer instanceof ArrayBuffer ? new Uint8Array(arrayBuffer) : arrayBuffer;
  // cellDates:false — Excel serial; Date+UTC ile gün kayması olmaz (TR 16.03 → 2026-03-16)
  const wb = XLSX.read(bytes, { type: "array", cellDates: false, cellNF: true });
  const sheetNames = wb.SheetNames || [];
  if (!sheetNames.length) {
    return { error: "Boş dosya", sheets: [] };
  }

  const sheets = sheetNames.map((sheetName) => {
    const ws = wb.Sheets[sheetName];
    const rowsRaw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: true });
    const rowsFmt = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: false });
    const headerRowIdx = findHeaderRowIndex(rowsFmt.length ? rowsFmt : rowsRaw);
    const headerRow = (rowsFmt[headerRowIdx] || rowsRaw[headerRowIdx] || []).map((c, i) => {
      const fmt = rowsFmt[headerRowIdx]?.[i];
      return fmt != null && fmt !== "" ? fmt : c;
    });
    const mapping = guessColumnMapping(headerRow);
    const previewRows = [];
    for (let r = headerRowIdx + 1; r < Math.min(rowsRaw.length, headerRowIdx + 8); r++) {
      previewRows.push(rowsFmt[r] || rowsRaw[r] || []);
    }
    return {
      id: sheetName,
      sheet_name: sheetName,
      header_row: headerRowIdx + 1,
      headers: headerRow.map((h, col) => ({ col, label: cellStr(h) || `Sütun ${col + 1}` })),
      name_col: mapping.nameCol >= 0 ? mapping.nameCol : 0,
      date_col: mapping.dateCol >= 0 ? mapping.dateCol : 1,
      row_count: Math.max(0, rowsRaw.length - headerRowIdx - 1),
      preview_rows: previewRows,
      _rowsRaw: rowsRaw,
      _rowsFmt: rowsFmt,
      _headerRowIdx: headerRowIdx,
    };
  });

  return { error: null, sheets, sheetNames };
}

export function extractHireRowsFromSheet(sheet, nameCol, dateCol) {
  const { withDate } = extractHireSheetRows(sheet, nameCol, dateCol);
  return withDate;
}

/** Ad + tarih satırları; Excel'de tarih boş olanlar ayrı listelenir */
export function extractHireSheetRows(sheet, nameCol, dateCol) {
  const withDate = [];
  const missingDate = [];
  if (!sheet?._rowsRaw) return { withDate, missingDate };
  const headerIdx = sheet._headerRowIdx ?? 0;
  const rowsFmt = sheet._rowsFmt || sheet._rowsRaw;
  const rowsRaw = sheet._rowsRaw;
  for (let r = headerIdx + 1; r < rowsRaw.length; r++) {
    const name = cellStr(rowsFmt[r]?.[nameCol] ?? rowsRaw[r]?.[nameCol]).replace(/\r\n/g, " ");
    if (!name || name.length < 2) continue;
    if (/^toplam$/i.test(name)) break;
    const dateCell = pickHireDateCellValue(rowsRaw[r]?.[dateCol], rowsFmt[r]?.[dateCol]);
    const hireDate = parseHireDateCell(dateCell);
    if (!hireDate) {
      missingDate.push({ excel_name: name, row_number: r + 1 });
      continue;
    }
    withDate.push({
      excel_name: name,
      ise_giris_tarihi: hireDate,
      row_number: r + 1,
    });
  }
  return { withDate, missingDate };
}

function nameMatchKeys(name) {
  const raw = cellStr(name);
  if (!raw) return [];
  const keys = new Set();
  keys.add(normalizeTr(raw));
  const parts = raw.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    keys.add(normalizeTr(`${parts[0]} ${parts[parts.length - 1]}`));
    keys.add(normalizeTr(parts[parts.length - 1]));
  }
  return [...keys].filter(Boolean);
}

function agentNameKeys(agent) {
  const keys = new Set();
  const ad = cellStr(agent.ad_soyad || agent.name);
  nameMatchKeys(ad).forEach((k) => keys.add(k));
  const user = cellStr(agent.kullanici_adi);
  if (user) {
    keys.add(normalizeTr(user));
    keys.add(normalizeTr(user.replace(/[._-]+/g, " ")));
  }
  return keys;
}

function scoreAgentForExcelRow(agent, excelName) {
  const excelNorm = normalizeTr(cellStr(excelName));
  const adNorm = normalizeTr(cellStr(agent.ad_soyad || agent.name || ""));
  let score = 0;
  if (adNorm && adNorm === excelNorm) score += 120;
  const parts = cellStr(excelName).split(/\s+/).filter(Boolean);
  if (parts.length >= 2 && adNorm) {
    const fl = normalizeTr(`${parts[0]} ${parts[parts.length - 1]}`);
    if (adNorm === fl) score += 85;
  }
  const userNorm = normalizeTr(cellStr(agent.kullanici_adi).replace(/[._-]+/g, " "));
  if (userNorm && userNorm === excelNorm) score += 75;
  if (!normalizePanelHireDate(agent.ise_giris_tarihi)) score += 60;
  if (Number(agent.status) === 1) score += 15;
  return score;
}

function pickBestAgentCandidate(candidates, excelName) {
  if (!candidates?.length) return null;
  let best = null;
  let bestScore = -1;
  let secondScore = -1;
  for (const agent of candidates) {
    const score = scoreAgentForExcelRow(agent, excelName);
    if (score > bestScore) {
      secondScore = bestScore;
      bestScore = score;
      best = agent;
    } else if (score > secondScore) {
      secondScore = score;
    }
  }
  if (bestScore < 40) return null;
  if (secondScore >= 0 && bestScore - secondScore < 10 && bestScore < 100) return null;
  return best;
}

export function matchAgentByExcelName(agents, excelName) {
  const excelNorm = normalizeTr(cellStr(excelName));
  if (!excelNorm) return null;
  const excelKeys = nameMatchKeys(excelName);

  let candidates = agents.filter((a) => agentNameKeys(a).has(excelNorm));
  if (candidates.length) return pickBestAgentCandidate(candidates, excelName);

  const parts = cellStr(excelName).split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const firstLast = normalizeTr(`${parts[0]} ${parts[parts.length - 1]}`);
    candidates = agents.filter((a) => agentNameKeys(a).has(firstLast));
    const picked = pickBestAgentCandidate(candidates, excelName);
    if (picked) return picked;
  }

  for (const key of excelKeys) {
    if (!key || key.length < 4) continue;
    candidates = agents.filter((a) => agentNameKeys(a).has(key));
    if (candidates.length === 1) return candidates[0];
    const picked = pickBestAgentCandidate(candidates, excelName);
    if (picked) return picked;
  }
  return null;
}

export function buildHireDateImportPreview(excelRows, agents, agentOverrides = {}) {
  const matched = [];
  const unmatched = [];
  const unchanged = [];
  const matchedAgentIds = new Set();

  excelRows.forEach((row) => {
    const overrideId = agentOverrides[row.excel_name];
    let agent = null;
    if (overrideId) {
      agent = agents.find((a) => Number(a.id) === Number(overrideId));
    }
    if (!agent) {
      agent = matchAgentByExcelName(agents, row.excel_name);
    }
    if (!agent) {
      unmatched.push({ ...row, match_status: "unmatched" });
      return;
    }
    matchedAgentIds.add(Number(agent.id));
    const oldDate = normalizePanelHireDate(agent.ise_giris_tarihi);
    const newDate = normalizePanelHireDate(row.ise_giris_tarihi) || parseHireDateCell(row.ise_giris_tarihi);
    const item = {
      ...row,
      agent_id: agent.id,
      agent_name: agent.ad_soyad,
      old_date: oldDate,
      new_date: newDate,
      panel_hire_was_empty: !oldDate && !!newDate,
      match_status: "matched",
      will_change: oldDate !== newDate,
    };
    if (oldDate === newDate) {
      unchanged.push(item);
    } else {
      matched.push(item);
    }
  });

  const panelMissingHire = agents.filter((a) => !normalizePanelHireDate(a.ise_giris_tarihi));
  const panelMissingNotInExcel = panelMissingHire.filter((a) => !matchedAgentIds.has(Number(a.id)));

  return {
    to_update: matched,
    unchanged,
    unmatched,
    update_count: matched.length,
    unmatched_count: unmatched.length,
    unchanged_count: unchanged.length,
    panel_missing_hire_count: panelMissingHire.length,
    panel_missing_not_reached: panelMissingNotInExcel.map((a) => ({
      agent_id: a.id,
      agent_name: a.ad_soyad,
      kullanici_adi: a.kullanici_adi,
    })),
  };
}
